import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Play, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdventureSetup } from "@/components/play/AdventureSetup";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { tFormat } from "@/lib/i18n/t-format";
import { cacheAdventure } from "@/lib/adventure-cache";
import type { Translations } from "@/lib/i18n/types";
import type { JoinedAdventureItem } from "@/hooks/useIndexData";
import { useLocale } from "@/hooks/useLocale";
import { fmtDate } from "@/lib/locale-format";

function formatTimeAgo(dateStr: string, locale: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return fmtDate(new Date(dateStr), locale);
}

interface StartGameDialogProps {
  t: Translations;
  showNewGame: boolean;
  setShowNewGame: (v: boolean) => void;
  selectedWorldId: string;
  setSelectedWorldId: (v: string) => void;
  playableWorlds: any[];
  adventures: any[];
  joinedAdventures: JoinedAdventureItem[];
  creatingGame: boolean;
  setCreatingGame: (v: boolean) => void;
  navigate: (path: string) => void;
}

export function StartGameDialog({
  t, showNewGame, setShowNewGame, selectedWorldId, setSelectedWorldId,
  playableWorlds, adventures, joinedAdventures, creatingGame, setCreatingGame, navigate,
}: StartGameDialogProps) {
  const { locale } = useLocale();
  const [showSetup, setShowSetup] = useState(false);

  const worldAdventures = selectedWorldId
    ? [
        ...(adventures || []).filter((a: any) => a.world_id === selectedWorldId),
        ...(joinedAdventures || []).filter((j: JoinedAdventureItem) => j.adventure?.world_id === selectedWorldId)
          .map((j: JoinedAdventureItem) => ({ ...j.adventure, _source: "joined", _characterName: j.character_name })),
      ]
    : [];

  const handleClose = (open: boolean) => {
    setShowNewGame(open);
    if (!open) { setSelectedWorldId(""); setShowSetup(false); }
  };

  return (
    <Dialog open={showNewGame} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="sr-only">{t.idx_start_new_game}</DialogTitle></DialogHeader>
        {!selectedWorldId ? (
          <div className="space-y-4">
            <h3 className="font-semibold">{t.idx_select_world}</h3>
            <Select value={selectedWorldId} onValueChange={setSelectedWorldId}>
              <SelectTrigger><SelectValue placeholder={t.idx_choose_world} /></SelectTrigger>
              <SelectContent>
                {playableWorlds.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">{t.idx_no_worlds_available}</div>
                ) : (
                  playableWorlds.map((w: any) => (
                    <SelectItem key={w.id} value={w.id}>
                      <span className="flex items-center gap-2">
                        <span>{w.icon || "🌍"}</span>
                        <span>{w.name}</span>
                        {w.owner_name && <span className="text-muted-foreground text-xs">by {w.owner_name}</span>}
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        ) : showSetup || worldAdventures.length === 0 ? (
          <AdventureSetup
            worldId={selectedWorldId}
            worldName={playableWorlds.find((w: any) => w.id === selectedWorldId)?.name || "World"}
            isCreating={creatingGame}
            onCancel={() => {
              if (worldAdventures.length > 0) setShowSetup(false);
              else { setSelectedWorldId(""); setShowNewGame(false); }
            }}
            onStart={async (config) => {
              setCreatingGame(true);
              try {
                const { data: { session } } = await supabase.auth.getSession();
                if (!session?.user) throw new Error("Not authenticated");
                const user = session.user;
                const playerChar = config.customCharacter
                  ? { name: config.customCharacter.name, description: config.customCharacter.description, stats: {}, inventory: [], conditions: [] }
                  : config.selectedCharacterId
                    ? await (async () => {
                        const { data: charEntity } = await supabase.from("entities").select("name, description, properties, image_url").eq("id", config.selectedCharacterId!).single();
                        if (charEntity) return { name: charEntity.name, description: charEntity.description || "", stats: (charEntity.properties as any)?.stats || {}, inventory: [], conditions: [], image_url: charEntity.image_url || undefined };
                        return { name: "", stats: {}, inventory: [], conditions: [], description: "" };
                      })()
                    : { name: "", stats: {}, inventory: [], conditions: [], description: "" };
                const { data, error } = await supabase.from("adventures")
                  .insert({
                    world_id: selectedWorldId,
                    user_id: user.id,
                    name: config.name,
                    is_multiplayer: config.isMultiplayer,
                    turn_mode: config.turnMode,
                    scenario_id: config.scenarioId,
                    difficulty: config.difficulty,
                    difficulty_config: config.difficultyConfig,
                    player_character: playerChar,
                    state: { turn_count: 0, current_location: "", active_npcs: [], context_tags: [], variables: { ...config.initialState }, invite_mode: config.inviteMode || "link" },
                  })
                  .select().single();
                if (error) throw error;
                if (config.isMultiplayer) {
                  await supabase.from("adventure_players").insert({
                    adventure_id: data.id, user_id: user.id, is_host: true, character_name: config.customCharacter?.name || "",
                  });
                }
                cacheAdventure(data);
                setShowNewGame(false);
                setSelectedWorldId("");
                setShowSetup(false);
                navigate(`/play/${data.id}`);
              } catch (err: any) {
                toast.error(tFormat(t.idx_start_game_failed, { error: err.message }));
              } finally {
                setCreatingGame(false);
              }
            }}
          />
        ) : (
          <div className="space-y-5">
            <div className="space-y-1">
              <h3 className="font-semibold text-lg">{playableWorlds.find((w: any) => w.id === selectedWorldId)?.name || "World"}</h3>
              <p className="text-sm text-muted-foreground">{t.idx_select_or_new_adventure}</p>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t.wb_continue_playing}</label>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {worldAdventures.map((adv: any) => {
                  const turnCount = (adv.state as any)?.turn_count || 0;
                  const pc = adv.player_character;
                  const charName = adv._characterName || pc?.name || null;
                  return (
                    <button
                      key={adv.id}
                      onClick={() => { cacheAdventure(adv); setShowNewGame(false); setSelectedWorldId(""); navigate(`/play/${adv.id}`); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors text-left"
                    >
                      <Play className="w-4 h-4 text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{adv.name}</p>
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                          {charName && <span>{charName}</span>}
                          <span>{t.idx_turn} {turnCount}</span>
                          <span>·</span>
                          <span>{formatTimeAgo(adv.updated_at, locale)}</span>
                          {adv.is_multiplayer && (
                            <Badge variant="secondary" className="text-[9px] px-1 py-0 h-3.5">
                              <Users className="w-2.5 h-2.5 mr-0.5" />MP
                            </Badge>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">{t.idx_or}</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <Button className="w-full gap-2" onClick={() => setShowSetup(true)}>
              <Plus className="w-4 h-4" />{t.idx_new_game}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
